import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  MEASUREMENT_MODE_LABEL,
  MEASUREMENT_MODES,
} from '@obraplan/core/domain/planning/team-measurement';
import {
  PROJECT_STATUS_LABEL,
  type Project,
  type ProjectStatus,
} from '@obraplan/core/domain/structure/project';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Label } from '@/components/ui/input';
import { SupplyProjectField } from './SupplyProjectField';

const STATUS_ORDER: readonly ProjectStatus[] = [
  'PLANNING',
  'ACTIVE',
  'PAUSED',
  'FINISHED',
  'ARCHIVED',
];

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD.')
  .or(z.literal(''));

const formSchema = z
  .object({
    name: z.string().trim().min(1, 'Nome é obrigatório.').max(160),
    client: z.string().trim().max(160),
    address: z.string().trim().max(300),
    technicalManager: z.string().trim().max(160),
    status: z.enum(['PLANNING', 'ACTIVE', 'PAUSED', 'FINISHED', 'ARCHIVED']),
    startDate: isoDate,
    endDate: isoDate,
    riskHorizonDays: z.coerce.number().int().min(0).max(90),
    requireDistinctInspector: z.boolean(),
    measurementMode: z.enum(['POR_PESSOA', 'POR_EQUIPE']),
    externalId: z.string().trim().max(64),
  })
  .refine(
    (value) => value.endDate === '' || value.startDate === '' || value.startDate <= value.endDate,
    {
      message: 'O término não pode ser anterior ao início.',
      path: ['endDate'],
    },
  );

export type ProjectFormValues = z.infer<typeof formSchema>;

/**
 * Edição da obra.
 *
 * O código não está aqui: ele identifica a obra no sistema e em qualquer backup já
 * exportado — trocá-lo depois quebraria a correspondência. Nome, cliente, endereço e
 * responsável técnico são texto livre; situação, horizonte de risco e a exigência de
 * conferente distinto mudam comportamento de fases adiante (RN-066, RN-101) e por isso
 * ficam visíveis desde já.
 *
 * O responsável técnico entrou na Fase 18 e é o único campo daqui que existe **para o
 * papel**: ele não muda comportamento nenhum do sistema, e sai no cabeçalho do RDO.
 */
export function ProjectEditDialog({
  open,
  onOpenChange,
  project,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project | undefined;
  onSubmit: (values: ProjectFormValues) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      client: '',
      address: '',
      technicalManager: '',
      status: 'PLANNING',
      startDate: '',
      endDate: '',
      riskHorizonDays: 0,
      requireDistinctInspector: false,
      measurementMode: 'POR_PESSOA',
      externalId: '',
    },
  });

  const { reset } = form;

  useEffect(() => {
    if (open && project !== undefined) {
      reset({
        name: project.name,
        client: project.client ?? '',
        address: project.address ?? '',
        technicalManager: project.technicalManager ?? '',
        status: project.status,
        startDate: project.startDate ?? '',
        endDate: project.endDate ?? '',
        riskHorizonDays: project.riskHorizonDays,
        requireDistinctInspector: project.requireDistinctInspector,
        measurementMode: project.measurementMode,
        externalId: project.externalId ?? '',
      });
    }
  }, [open, project, reset]);

  if (project === undefined) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar obra</DialogTitle>
          <DialogDescription>
            Código <span className="font-mono">{project.code}</span> — o código não muda depois de
            criado.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((values) => onSubmit(values))(event);
          }}
        >
          <Field
            label="Nome"
            htmlFor="edit-project-name"
            required
            error={form.formState.errors.name?.message}
          >
            <Input id="edit-project-name" autoFocus {...form.register('name')} />
          </Field>

          <Field label="Cliente" htmlFor="edit-project-client">
            <Input id="edit-project-client" {...form.register('client')} />
          </Field>

          <Field label="Endereço" htmlFor="edit-project-address">
            <Input id="edit-project-address" {...form.register('address')} />
          </Field>

          {/*
            O nome que assina o RDO. Texto livre, e não um perfil: o engenheiro do CREA
            costuma não operar o sistema, e um perfil desativado deixaria documento já
            emitido sem assinatura.
          */}
          <Field
            label="Responsável técnico"
            htmlFor="edit-project-technical-manager"
            hint="Sai no cabeçalho do Relatório Diário de Obra."
          >
            <Input id="edit-project-technical-manager" {...form.register('technicalManager')} />
          </Field>

          {/*
            O destino só podia ser escolhido na criação, e trocá-lo exigia recriar a obra
            inteira — com a estrutura e o cronograma dentro. O campo é o mesmo do
            cadastro, e o valor vazio aqui **desliga** a integração de propósito: é a
            pessoa dizendo que esta obra deixou de requisitar.
          */}
          <Field
            label="Destino no sistema de compras"
            htmlFor="edit-project-external-id"
            hint="Escolha o projeto que recebe as requisições desta obra. Sem ele, a ciência na aba de Suprimentos continua sendo só um registro."
          >
            <SupplyProjectField
              id="edit-project-external-id"
              registration={form.register('externalId')}
            />
          </Field>

          <Field label="Situação" htmlFor="edit-project-status" required>
            <select
              id="edit-project-status"
              className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
              {...form.register('status')}
            >
              {STATUS_ORDER.map((status) => (
                <option key={status} value={status}>
                  {PROJECT_STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Início"
              htmlFor="edit-project-start"
              error={form.formState.errors.startDate?.message}
            >
              <Input id="edit-project-start" type="date" {...form.register('startDate')} />
            </Field>
            <Field
              label="Término previsto"
              htmlFor="edit-project-end"
              error={form.formState.errors.endDate?.message}
            >
              <Input id="edit-project-end" type="date" {...form.register('endDate')} />
            </Field>
          </div>

          <Field
            label="Horizonte de risco (dias)"
            htmlFor="edit-project-horizon"
            hint="Com quantos dias de antecedência o material em falta vira alerta."
            error={form.formState.errors.riskHorizonDays?.message}
          >
            <Input
              id="edit-project-horizon"
              type="number"
              min={0}
              max={90}
              {...form.register('riskHorizonDays')}
            />
          </Field>

          <div className="flex items-start gap-2">
            <input
              id="edit-project-distinct"
              type="checkbox"
              className="mt-1"
              {...form.register('requireDistinctInspector')}
            />
            <div className="flex flex-col gap-0.5">
              <Label htmlFor="edit-project-distinct">
                Exigir conferente diferente do planejador
              </Label>
              <p className="text-xs text-(--color-content-muted)">
                Vale a partir da conferência diária. Como não há senha, isto é controle operacional,
                não sigilo.
              </p>
            </div>
          </div>

          {/*
            A quantidade da Conferência é de cada pessoa ou de cada equipe (RN-405). A troca vale
            para os dias ainda não conferidos: o que já foi medido fica como foi (D3).
          */}
          <Field
            label="Medição na Conferência"
            htmlFor="edit-project-measurement-mode"
            hint="Por equipe: a quantidade é lançada uma vez por equipe em cada frente e serviço; as horas continuam de cada pessoa. Quem não tem equipe e o terceirizado medem por pessoa. Vale para os dias ainda não conferidos."
          >
            <select
              id="edit-project-measurement-mode"
              className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
              {...form.register('measurementMode')}
            >
              {MEASUREMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {MEASUREMENT_MODE_LABEL[mode]}
                </option>
              ))}
            </select>
          </Field>

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
